import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import {
  Row,
  Container,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Label,
} from "reactstrap"
import SplineArea from "./SplineArea"
import BarChart from "./barChart"
import DonutChart from "./donutchart"
import ChartApex from "./chartapex"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import {
  IncidentReportSummary,
  SurveyReportSummary,
  ContentReportSummary,
} from "api/reports"
import DatePicker from "react-datepicker"

const Dashboard = props => {
  useEffect(() => {
    document.title = "Dashboard | CA Portal"
  }, [])

  const [incidentData, setIncidentData] = useState(null)
  const [surveyData, setSurveyData] = useState(null)
  const [contentData, setContentData] = useState(null)

  const [errorIncident, setErrorIncident] = useState(null)
  const [errorSurvey, setErrorSurvey] = useState(null)
  const [errorContent, setErrorContent] = useState(null)

  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1)
  )
  const [endDate, setEndDate] = useState(
    new Date(new Date().getFullYear(), 11, 31)
  )
  const [otherParams] = useState({
    searchColumns: [],
    pageSize: 100,
    pageNumber: 0,
    sortBy: "string",
    ascendingSort: true,
  })

  useEffect(() => {
    const initialStartDate = formatDate(startDate)
    const initialEndDate = formatDate(endDate)

    fetchIncidentData({
      fromDate: initialStartDate,
      toDate: initialEndDate,
      ...otherParams,
    })
    fetchSurveyData({
      fromDate: initialStartDate,
      toDate: initialEndDate,
      ...otherParams,
    })
    fetchContentData({
      fromDate: initialStartDate,
      toDate: initialEndDate,
      ...otherParams,
    })
  }, [])

  const fetchIncidentData = async params => {
    setErrorIncident(null)
    try {
      const incidentRes = await IncidentReportSummary(params)
      setIncidentData(incidentRes.data)
    } catch (error) {
      console.error("Error fetching incident data:", error)
      setErrorIncident("Failed to load incidents")
      setIncidentData(null)
    }
  }

  const fetchSurveyData = async params => {
    setErrorSurvey(null)
    try {
      const surveyRes = await SurveyReportSummary(params)
      setSurveyData(surveyRes.data)
    } catch (error) {
      console.error("Error fetching survey data:", error)
      setErrorSurvey("Failed to load survey report")
      setSurveyData(null)
    }
  }

  const fetchContentData = async params => {
    setErrorContent(null)
    try {
      const contentRes = await ContentReportSummary(params)
      setContentData(contentRes.data)
    } catch (error) {
      console.error("Error fetching content data:", error)
      setErrorContent("Failed to load content report")
      setContentData(null)
    }
  }

  const handleApply = () => {
    const formattedParams = {
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate),
      ...otherParams,
    }
    fetchIncidentData(formattedParams)
    fetchSurveyData(formattedParams)
    fetchContentData(formattedParams)
  }

  const handleStartDateChange = date => setStartDate(date)
  const handleEndDateChange = date => setEndDate(date)

  const formatDate = date => {
    if (!(date instanceof Date)) {
      date = new Date(date)
    }
    if (!(date instanceof Date) || isNaN(date.getDate())) {
      return ""
    }
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // robust data check
  const hasData = (d, type) => {
    if (!d) return false

    // incidents
    if (type === "incident") {
      if (Array.isArray(d) && d.length > 0) {
        return d.some(item =>
          Object.values(item).some(val => typeof val === "number" && val > 0)
        )
      }
      if (Array.isArray(d.series)) {
        return d.series.some(
          series =>
            Array.isArray(series.data) &&
            series.data.some(val => typeof val === "number" && val > 0)
        )
      }
    }

    // surveys
    if (type === "survey") {
      if (Array.isArray(d)) {
        return d.some(item =>
          Object.values(item).some(val => typeof val === "number" && val > 0)
        )
      }
      if (Array.isArray(d.data)) {
        return d.data.some(val => typeof val === "number" && val > 0)
      }
    }

    // content
    if (type === "content") {
      if (Array.isArray(d)) {
        return d.some(item =>
          Object.values(item).some(val => typeof val === "number" && val > 0)
        )
      }
      if (Array.isArray(d.categories) && d.categories.length > 0) {
        return true
      }
      if (Array.isArray(d.series)) {
        return d.series.some(
          series =>
            Array.isArray(series.data) &&
            series.data.some(val => typeof val === "number" && val > 0)
        )
      }
    }

    // fallback
    if (Array.isArray(d)) return d.length > 0
    if (typeof d === "object") return Object.keys(d).length > 0

    return false
  }

  const renderSection = ({ error, data, type, ChartComponent, chartProps }) => {
    if (error) {
      return <p className="text-danger">{error}</p>
    }
    if (!hasData(data, type)) {
      return <p className="text-muted">No data available.</p>
    }
    return <ChartComponent {...chartProps} data={data} />
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Home" breadcrumbItem="Dashboard" />

          {/* Filter Row */}
          <Row className="mb-4 align-items-end">
            <Col md={4}>
              <Label className="form-label">Start Date</Label>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                className="form-control"
              />
            </Col>

            <Col md={4}>
              <Label className="form-label">End Date</Label>
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                className="form-control"
              />
            </Col>

            <Col md={2} className="d-flex">
              <Button
                color="primary"
                className="me-2 flex-grow-1"
                onClick={handleApply}
              >
                Apply Filter
              </Button>
            </Col>
          </Row>

          {/* Incidents */}
          <Row className="mb-4">
            <Col md={8}>
              <Card className="h-100">
                <CardBody>
                  <CardTitle className="mb-4">Incidents Report</CardTitle>
                  <Row>
                    {renderSection({
                      error: errorIncident,
                      data: incidentData,
                      type: "incident",
                      ChartComponent: SplineArea,
                      chartProps: {
                        dataColors:
                          '["--bs-success", "--bs-primary", "--bs-warning","--bs-secondary"]',
                      },
                    })}
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 d-flex flex-column">
                <CardBody className="d-flex flex-column">
                  <CardTitle className="mb-4">Incidents Chart</CardTitle>
                  {renderSection({
                    error: errorIncident,
                    data: incidentData,
                    type: "incident",
                    ChartComponent: DonutChart,
                    chartProps: {
                      dataColors: '["--bs-warning","--bs-secondary"]',
                    },
                  })}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Survey + Content */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100 d-flex flex-column">
                <CardBody className="d-flex flex-column">
                  <CardTitle className="mb-4">Survey Report</CardTitle>
                  {renderSection({
                    error: errorSurvey,
                    data: surveyData,
                    type: "survey",
                    ChartComponent: BarChart,
                    chartProps: { dataColors: '["--bs-success"]' },
                  })}
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 d-flex flex-column">
                <CardBody className="d-flex flex-column">
                  <CardTitle className="mb-4">Content Report</CardTitle>
                  {renderSection({
                    error: errorContent,
                    data: contentData,
                    type: "content",
                    ChartComponent: ChartApex,
                    chartProps: { dataColors: '["--bs-success"]' },
                  })}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
}

export default withTranslation()(Dashboard)
